import { Alert, Box, Button, Typography } from '@mui/material'
import {
  CarrierVisitStatus,
  ContainerStatus,
  YardInternalMovesApiYardInternalMovesCreateMoveRequest,
} from '@storage/app/api'
import { ConfirmationDialogContainer } from '@storage/components/ConfirmationDialogContainer'
import useFormWithSchema from '@storage/hooks/use-form-with-schema.hook'
import { useStores } from '@storage/hooks/use-stores.hook'
import { moveContainerService } from '@storage/services'
import { TenantConfigStore } from '@storage/stores/tenantConfig.store'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { ContainerMovementInfo } from '@tom-ui/utils'
import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { ManualPlanningDialogStore } from '../../stores/manual-planning-dialog.store'
import { CTAdvancedSearchField } from './CTAdvancedSearchField'
import { ContainerTurnoversQuickFilter } from './components'
import { mapFormValuesToFilterDto } from './container-turnovers-filter-form.mapper'
import {
  ContainerTurnoversFilterFormProfile,
  ContainerTurnoversFilterFormProfileKey,
  defaultValues,
} from './container-turnovers-filter-form.profile'
import { schema } from './container-turnovers-filter-form.schema'

export const ContainerTurnoversAdvancedFilterFormId = 'ContainerTurnoversAdvancedFilterForm'

interface Props {
  filterDefaultValues?: Partial<ContainerTurnoversFilterFormProfile>
  containersNumberMatchingFilter: number
  filter: ContainerTurnoversFilterFormProfile
  advancedFiltersShown: boolean
  disabledFilters?: ContainerTurnoversFilterFormProfileKey[]
  onToggleAdvancedFilters: () => void
  onPlanManually?: () => void
  onPlanOnYard?: () => void
  onMoveManually?: (containers: ContainerMovementInfo[]) => void
  onSubmit: (values: ContainerTurnoversFilterFormProfile) => void
  tenantConfigStore?: TenantConfigStore
  manualPlanningDialogStore?: ManualPlanningDialogStore
}

export const ContainerTurnoversFilterForm = observer(
  ({
    filterDefaultValues,
    filter,
    advancedFiltersShown,
    containersNumberMatchingFilter,
    disabledFilters,
    onToggleAdvancedFilters,
    onPlanManually,
    onMoveManually,
    onSubmit,
    tenantConfigStore,
    manualPlanningDialogStore,
  }: Props) => {
    const { carrierVisitAllocationRulesV2UIStore } = useStores()

    const { palette } = useMinimalsTheme()
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false)
    const form = useFormWithSchema<ContainerTurnoversFilterFormProfile>(schema, filter)

    const { control, formState, reset, handleSubmit, watch, setValue } = form

    const { t } = useTranslate()

    const { carrierVisitStore, companyStore, snackbarStore } = useStores()

    const handleClearAll = useCallback(() => {
      for (const key of Object.keys(filter)) {
        const profileKey = key as keyof ContainerTurnoversFilterFormProfile

        setValue(profileKey, filter[profileKey])
      }
    }, [filter, setValue])

    useEffect(() => {
      handleClearAll()
    }, [setValue, filter, handleClearAll])

    const debouncedSubmit = debounce((formValues: ContainerTurnoversFilterFormProfile) => {
      onSubmit(formValues)
    }, 500)

    useEffect(() => {
      const { unsubscribe } = watch((formValues, { name, type }) => {
        if (!advancedFiltersShown) {
          if (type === 'change' && name === 'containerOrReferenceNumber') {
            debouncedSubmit(formValues as ContainerTurnoversFilterFormProfile)
          } else {
            onSubmit(formValues as ContainerTurnoversFilterFormProfile)
          }
        }
      })

      return () => {
        unsubscribe()
        debouncedSubmit.cancel()
      }
    }, [reset, onSubmit, watch, advancedFiltersShown, debouncedSubmit])

    useEffect(() => {
      const loadCarrierVisitOptions = async () => {
        await carrierVisitStore.loadList({
          query: [
            CarrierVisitStatus.Arrived,
            CarrierVisitStatus.Expected,
            CarrierVisitStatus.InOperation,
          ],
        })
      }

      const fetchData = async () => {
        await companyStore.loadCompanies()
      }

      fetchData()
      loadCarrierVisitOptions()
    }, [carrierVisitStore])

    const handleToggleAdvancedFilters = () => {
      if (advancedFiltersShown) {
        handleClearAll()
      }

      onToggleAdvancedFilters()
    }

    // Reset all fields to default except containerNumber
    useEffect(() => {
      const resetAllExceptContainerOrReferenceNumber = () => {
        reset({
          ...defaultValues,
          ...filterDefaultValues,
          locationStatus: defaultValues.locationStatus,
          containerOrReferenceNumber: filter.containerOrReferenceNumber,
        })
      }

      if (filter.containerOrReferenceNumber) {
        resetAllExceptContainerOrReferenceNumber()
      }
    }, [filter.containerOrReferenceNumber, filterDefaultValues, reset, setValue])

    const handleMoveContainers = async () => {
      const request: YardInternalMovesApiYardInternalMovesCreateMoveRequest = {
        createMovesDto: {
          filter: mapFormValuesToFilterDto(filter, true),
        },
      }
      try {
        await moveContainerService.moveContainer(request)
        snackbarStore.showMessage(
          t('containersSuccessfullyMoved', 'The containers moves have been triggered'),
          'success',
        )
        handleClearAll()
      } catch (e) {
        snackbarStore.showMessage(
          t('containersMovedFailed', 'The containers moves were not triggered'),
          'error',
        )
      } finally {
        setIsConfirmationDialogOpen(false)
      }
    }

    const isFilterEqualDefault = useMemo(() => isEqual(filter, defaultValues), [filter])

    const handleConfirmAllocation = () => {
      carrierVisitAllocationRulesV2UIStore.toggleDangerousGoodsDialog()
      carrierVisitAllocationRulesV2UIStore.allocationRequest(
        mapFormValuesToFilterDto(filter, true),
        containersNumberMatchingFilter,
        () => manualPlanningDialogStore?.planningSuccessfulEffect(),
      )
    }

    return (
      <FormProvider {...form}>
        <Box sx={{ display: 'flex', flexFlow: 'column', gap: 2 }}>
          <Box
            id={ContainerTurnoversAdvancedFilterFormId}
            onReset={() => reset()}
            component='form'
            autoComplete='off'
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <CTAdvancedSearchField
              control={control}
              formState={formState}
              handleToggleAdvancedFilters={handleToggleAdvancedFilters}
              advancedFiltersShown={advancedFiltersShown}
              tenantConfigStore={tenantConfigStore}
              companies={companyStore.companies}
            />
          </Box>
          {!advancedFiltersShown && (
            <ContainerTurnoversQuickFilter
              disabledFilters={disabledFilters}
              showClearAll={
                !isFilterEqualDefault &&
                !carrierVisitAllocationRulesV2UIStore.selectedAllocationRule
              }
              onClearAll={() => reset()}
              tenantConfigStore={tenantConfigStore}
            />
          )}

          {containersNumberMatchingFilter > 1 && !isFilterEqualDefault && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  borderRadius: '8px',
                  p: 1,
                  bgcolor: palette.grey[200],
                }}
              >
                <Typography variant='subtitle2' data-cy='container-selection'>
                  {t('nContainersAreSelected', { n: containersNumberMatchingFilter })}
                </Typography>
              </Box>
              {(tenantConfigStore && !tenantConfigStore.skipYardPlanning) || !tenantConfigStore ? (
                <Button
                  variant='contained'
                  onClick={onPlanManually}
                  data-cy='container-turnovers-plan-all'
                >
                  {t('planAll', 'Plan all')}
                </Button>
              ) : (
                tenantConfigStore.skipYardPlanning &&
                filter.locationStatus === ContainerStatus.OnTheYard &&
                containersNumberMatchingFilter > 1 && (
                  <Button variant='contained' onClick={() => setIsConfirmationDialogOpen(true)}>
                    {t('triggerMoves', 'Trigger moves')}
                  </Button>
                )
              )}
            </Box>
          )}

          <ConfirmationDialogContainer
            open={isConfirmationDialogOpen}
            title={t('confirmMoves', 'Confirm Moves')}
            content={
              <Typography>
                {t(
                  'confirmMoveText',
                  'Are you sure you want to move these containers? Only containers that have no triggered move will be triggered',
                )}
              </Typography>
            }
            onClose={() => setIsConfirmationDialogOpen(false)}
            onConfirm={handleMoveContainers}
          />
          <ConfirmationDialogContainer
            open={carrierVisitAllocationRulesV2UIStore.isDangerousGoodsDialogOpen}
            title={t('confirmAllocation', 'Confirm Allocation')}
            content={
              <Alert severity='warning'>{carrierVisitAllocationRulesV2UIStore.dialogText}</Alert>
            }
            onClose={() => carrierVisitAllocationRulesV2UIStore.toggleDangerousGoodsDialog()}
            onConfirm={handleConfirmAllocation}
          />
        </Box>
      </FormProvider>
    )
  },
)
